import type { DocMeta } from '@blocksuite/store';
import { ShareDocsListService } from '@pique/core/modules/share-doc';
import type { Collection, Filter } from '@pique/env/filter';
import { PublicPageMode } from '@pique/graphql';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo } from 'react';

import { filterPage, filterPageByRules } from './use-collection-manager';

export const useFilteredPageMetas = (
  pageMetas: DocMeta[],
  options: {
    trash?: boolean;
    filters?: Filter[];
    collection?: Collection;
  } = {}
) => {
  const shareDocsListService = useService(ShareDocsListService);
  const shareDocs = useLiveData(shareDocsListService.shareDocs?.list$);

  const getPublicMode = useCallback(
    (id: string) => {
      const mode = shareDocs?.find(
        (shareDoc: { id: string; mode: PublicPageMode }) => shareDoc.id === id
      )?.mode;
      return mode
        ? mode === PublicPageMode.Edgeless
          ? ('edgeless' as const)
          : ('page' as const)
        : undefined;
    },
    [shareDocs]
  );

  useEffect(() => {
    shareDocsListService.shareDocs?.revalidate();
  }, [shareDocsListService]);

  const filteredPageMetas = useMemo(
    () =>
      pageMetas.filter(pageMeta => {
        if (options.trash) {
          if (!pageMeta.trash) {
            return false;
          }
        } else if (pageMeta.trash) {
          return false;
        }
        const pageData = {
          meta: pageMeta,
          favorite: false,
          publicMode: getPublicMode(pageMeta.id),
          pageTitle: pageMeta.title || 'Untitled',
        };
        if (
          options.filters &&
          !filterPageByRules(options.filters, [], pageData)
        ) {
          return false;
        }

        if (options.collection && !filterPage(options.collection, pageData)) {
          return false;
        }

        return true;
      }),
    [pageMetas, options.trash, options.filters, options.collection, getPublicMode]
  );

  return filteredPageMetas;
};
